import axios from 'axios'

// No /api suffix — the subdomain dev-api.scribenza.com IS the API
// Ordinary CRUD should fail fast rather than hang a spinner forever.
const DEFAULT_TIMEOUT = 60_000
// Endpoints that run a model inline (generate / analyze) legitimately take
// far longer, so they pass `ai` as their per-request config.
const ai = { timeout: 300_000 }
// Public patient-portal calls carry the portal PIN in a header, which stays
// out of access logs (a query param doesn't).
const portalPinHeader = (pin) => (pin ? { 'X-Portal-Pin': pin } : {})

const api = axios.create({
  baseURL: import.meta.env.VITE_API_URL || '',
  headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
  withCredentials: true,
  timeout: DEFAULT_TIMEOUT,
})

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('scribenza_token')
  if (token) config.headers.Authorization = `Bearer ${token}`
  return config
})

api.interceptors.response.use(
  (res) => res,
  (err) => {
    if (err.response?.status === 401) {
      const hadToken = !!localStorage.getItem('scribenza_token')
      localStorage.removeItem('scribenza_token')
      // If a token was in use and we're on a protected page, force user back to login
      // so they see the session-expired prompt instead of a silently broken UI.
      const url = err.config?.url || ''
      const isAuthProbe = url.includes('/auth/login') || url.includes('/auth/register')
        || url.includes('/auth/verify-otp') || url.includes('/auth/resend-otp')
        || url.includes('/auth/forgot-password')
      const path = window.location.pathname
      const onAuthPage = path.startsWith('/login') || path.startsWith('/register')
        || path.startsWith('/verify-otp') || path.startsWith('/forgot-password')
      if (hadToken && !isAuthProbe && !onAuthPage) {
        // Defer to the next macrotask. If a 401 fires from a useQuery
        // inside a React render commit, assigning to window.location
        // synchronously interrupts the commit, which surfaces as
        // half-updated DOM / leaked subscriptions / "can't perform a
        // React state update on an unmounted component" warnings. A
        // 0-ms setTimeout lets the current promise settle and React
        // finish whatever it's flushing before we navigate away.
        setTimeout(() => {
          window.location.href = '/login?session_expired=1'
        }, 0)
      }
    }
    return Promise.reject(err)
  }
)

export default api

// Auth
export const authApi = {
  register:       (data) => api.post('/auth/register', data),
  verifyOtp:      (data) => api.post('/auth/verify-otp', data),
  resendOtp:      (data) => api.post('/auth/resend-otp', data),
  forgotPassword: (data) => api.post('/auth/forgot-password', data),
  resetPassword:  (data) => api.post('/auth/reset-password', data),
  login:          (data) => api.post('/auth/login', data),
  me:             ()     => api.get('/auth/me'),
  update:         (data) => api.put('/auth/me', data),
  logout:         ()     => api.post('/auth/logout'),
}

// Dashboard
export const dashboardApi = {
  get: () => api.get('/dashboard'),
}

// Meetings
export const meetingsApi = {
  list:           (page = 1) => api.get(`/meetings?page=${page}`),
  create:         (data)     => api.post('/meetings', data),
  get:            (id)       => api.get(`/meetings/${id}`),
  update:         (id, data) => api.put(`/meetings/${id}`, data),
  delete:         (id)       => api.delete(`/meetings/${id}`),
  saveTranscript: (id, data) => api.put(`/meetings/${id}/transcript`, data),
  search:         (q, scope) => api.get(`/search?q=${encodeURIComponent(q)}${scope ? `&scope=${encodeURIComponent(scope)}` : ''}`),
}

// Analysis
export const analysisApi = {
  analyze:          (id)      => api.post(`/meetings/${id}/analyze`, undefined, ai),
  get:              (id)      => api.get(`/meetings/${id}/analysis`),
  toggleActionItem: (id, idx) => api.put(`/meetings/${id}/action-items/${idx}`),
}

// Chat
export const chatApi = {
  history: (id)      => api.get(`/meetings/${id}/chat`),
  ask:     (id, msg) => api.post(`/meetings/${id}/chat`, { message: msg }, ai),
  clear:   (id)      => api.delete(`/meetings/${id}/chat`),
}

// Email
export const emailApi = {
  generate: (id)       => api.post(`/meetings/${id}/email`, undefined, ai),
  get:      (id)       => api.get(`/meetings/${id}/email`),
  update:   (id, data) => api.put(`/meetings/${id}/email`, data),
}

// Audio
export const audioApi = {
  upload: (id, file) => {
    const form = new FormData()
    form.append('audio', file)
    return api.post(`/meetings/${id}/audio`, form, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 600_000,
    })
  },
  status: (id) => api.get(`/meetings/${id}/audio/status`),
}

// Clinical Sections
export const clinicalSectionApi = {
  list:         ()           => api.get('/clinical-sections'),
  create:       (data)       => api.post('/clinical-sections', data),
  update:       (id, data)   => api.put(`/clinical-sections/${id}`, data),
  delete:       (id)         => api.delete(`/clinical-sections/${id}`),
  reorder:      (items)      => api.post('/clinical-sections/reorder', { items }),
  seedDefaults: ()           => api.post('/clinical-sections/seed-defaults'),
}

// Clinical Parameters
export const clinicalParamApi = {
  list:         ()           => api.get('/clinical-parameters'),
  create:       (data)       => api.post('/clinical-parameters', data),
  update:       (id, data)   => api.put(`/clinical-parameters/${id}`, data),
  delete:       (id)         => api.delete(`/clinical-parameters/${id}`),
  reorder:      (items)      => api.post('/clinical-parameters/reorder', { items }),
  seedDefaults: ()           => api.post('/clinical-parameters/seed-defaults'),
}

// Clinical Notes
export const clinicalNoteApi = {
  generate: (meetingId)       => api.post(`/meetings/${meetingId}/clinical-note`, undefined, ai),
  get:      (meetingId)       => api.get(`/meetings/${meetingId}/clinical-note`),
  update:   (meetingId, data) => api.put(`/meetings/${meetingId}/clinical-note`, data),
  delete:   (meetingId)       => api.delete(`/meetings/${meetingId}/clinical-note`),
}

// Billing
export const billingApi = {
  plans:          ()     => api.get('/plans'),
  status:         ()     => api.get('/billing'),
  history:        ()     => api.get('/billing/history'),
  checkout:       (data) => api.post('/billing/checkout', data),
  verifyRazorpay: (data) => api.post('/billing/razorpay/verify', data),
  capturePayPal:  (data) => api.post('/billing/paypal/capture', data),
  cancel:         ()     => api.post('/billing/cancel'),
}

// Analytics
export const analyticsApi = {
  get: (period = 30) => api.get('/analytics', { params: { period } }),
}

// Billing Codes
export const billingCodesApi = {
  list:         (meetingId)                   => api.get(`/meetings/${meetingId}/billing-codes`),
  generate:     (meetingId)                   => api.post(`/meetings/${meetingId}/billing-codes/generate`, undefined, ai),
  add:          (meetingId, data)             => api.post(`/meetings/${meetingId}/billing-codes`, data),
  update:       (meetingId, codeId, data)     => api.put(`/meetings/${meetingId}/billing-codes/${codeId}`, data),
  updateStatus: (meetingId, codeId, status)   => api.put(`/meetings/${meetingId}/billing-codes/${codeId}/status`, { status }),
  delete:       (meetingId, codeId)           => api.delete(`/meetings/${meetingId}/billing-codes/${codeId}`),
  export:       (meetingId)                   => api.get(`/meetings/${meetingId}/billing-codes/export`),
  emSuggestion: (meetingId)                   => api.get(`/meetings/${meetingId}/em-suggestion`, ai),
}

// Patients
export const patientsApi = {
  list:              (params)                    => api.get('/patients', { params }),
  create:            (data)                      => api.post('/patients', data),
  get:               (id)                        => api.get(`/patients/${id}`),
  update:            (id, data)                  => api.put(`/patients/${id}`, data),
  delete:            (id)                        => api.delete(`/patients/${id}`),
  timeline:          (id)                        => api.get(`/patients/${id}/timeline`),
  metrics:           (id)                        => api.get(`/patients/${id}/metrics`),
  summary:           (id)                        => api.get(`/patients/${id}/summary`),
  precharting:       (id)                        => api.get(`/patients/${id}/precharting`),
  longitudinal:      (id)                        => api.get(`/patients/${id}/longitudinal-summary`),
  // Family / household links (Phase 2 #25)
  listRelationships:   (id)                      => api.get(`/patients/${id}/relationships`),
  addRelationship:     (id, data)                => api.post(`/patients/${id}/relationships`, data),
  deleteRelationship:  (patientId, relId)        => api.delete(`/patients/${patientId}/relationships/${relId}`),
  cdsFlags:          ()                          => api.get('/patients/cds-flags'),
  addMetric:         (id, data)                  => api.post(`/patients/${id}/metrics`, data),
  addMetrics:        (id, metrics)               => api.post(`/patients/${id}/metrics/batch`, { metrics }),
  updateMetric:      (patientId, metricId, data) => api.put(`/patients/${patientId}/metrics/${metricId}`, data),
  deleteMetric:      (patientId, metricId)       => api.delete(`/patients/${patientId}/metrics/${metricId}`),
  deleteMetricGroup: (patientId, metricName)     => api.delete(`/patients/${patientId}/metrics/group/${encodeURIComponent(metricName)}`),
  extractMetrics:    (patientId, meetingId)      => api.post(`/patients/${patientId}/metrics/extract/${meetingId}`, undefined, ai),
}

// Differential diagnoses (Phase 2 #20)
export const differentialsApi = {
  generate: (meetingId) => api.get(`/meetings/${meetingId}/differentials`, ai),
}

// Visit document attachments (Phase 2 #17)
export const attachmentsApi = {
  list:     (meetingId)        => api.get(`/meetings/${meetingId}/attachments`),
  upload:   (meetingId, formData) => api.post(`/meetings/${meetingId}/attachments`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }),
  download: (id)               => api.get(`/attachments/${id}/download`, { responseType: 'blob' }),
  delete:   (id)               => api.delete(`/attachments/${id}`),
}

// Group visit participants (Phase 2 #26)
export const meetingParticipantsApi = {
  attach: (meetingId, data)        => api.post(`/meetings/${meetingId}/participants`, data),
  detach: (meetingId, patientId)   => api.delete(`/meetings/${meetingId}/participants/${patientId}`),
}

// Voice vocabulary (Phase 2 #24)
export const voiceVocabularyApi = {
  list:    ()         => api.get('/voice-vocabulary'),
  add:     (data)     => api.post('/voice-vocabulary', data),
  update:  (id, data) => api.put(`/voice-vocabulary/${id}`, data),
  delete:  (id)       => api.delete(`/voice-vocabulary/${id}`),
}

// Claim scrubbing (Phase 2 #21)
export const claimScrubberApi = {
  scrub: (meetingId) => api.post(`/meetings/${meetingId}/claim-scrub`),
}

// Security config — public site-key fetch + admin CRUD for reCAPTCHA keys
export const securityApi = {
  public:   ()      => api.get('/config/security'),
  show:     ()      => api.get('/admin/security-settings'),
  update:   (data)  => api.put('/admin/security-settings', data),
}

// Manual UPI payment flow (lifted from Jyotiza)
export const upiApi = {
  // Clinician-facing
  accounts:     ()                          => api.get('/upi/accounts'),
  submit:       (data)                      => api.post('/upi/payments', data),
  myPayments:   ()                          => api.get('/upi/my-payments'),
  // Admin
  adminList:    (params)                    => api.get('/admin/upi/payments', { params }),
  adminStats:   ()                          => api.get('/admin/upi/payments/stats'),
  adminApprove: (id)                        => api.put(`/admin/upi/payments/${id}/approve`),
  adminReject:  (id, rejection_note)        => api.put(`/admin/upi/payments/${id}/reject`, { rejection_note }),
  adminAccounts:          ()                => api.get('/admin/upi/accounts'),
  adminCreateAccount:     (form)            => api.post('/admin/upi/accounts', form, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }),
  adminUpdateAccount:     (id, form)        => api.post(`/admin/upi/accounts/${id}`, form, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }),
  adminDeleteAccount:     (id)              => api.delete(`/admin/upi/accounts/${id}`),
}

// Calendar integrations (Wave 2 Session 4 + Session 5)
export const calendarIntegrationsApi = {
  status:          ()                           => api.get('/integrations/calendars'),
  googleConnect:   ()                           => api.post('/integrations/google/connect'),
  microsoftConnect: ()                          => api.post('/integrations/microsoft/connect'),
  update:          (connectionId, data)         => api.put(`/integrations/calendars/${connectionId}`, data),
  disconnect:      (connectionId)               => api.delete(`/integrations/calendars/${connectionId}`),
  listExternalCalendars: (connectionId)         => api.get(`/integrations/calendars/${connectionId}/calendars`),
}

// Self-scheduling + intake (Phase 2 #13)
export const schedulingApi = {
  listSlots:        ()             => api.get('/scheduling/slots'),
  createSlot:       (data)         => api.post('/scheduling/slots', data),
  updateSlot:       (id, data)     => api.put(`/scheduling/slots/${id}`, data),
  deleteSlot:       (id)           => api.delete(`/scheduling/slots/${id}`),
  listAppointments: ()             => api.get('/scheduling/appointments'),
  updateAppointment: (id, data)    => api.put(`/scheduling/appointments/${id}`, data),
  // Wave 2 Session 1 — recurring slot templates
  listTemplates:    ()             => api.get('/scheduling/templates'),
  createTemplate:   (data)         => api.post('/scheduling/templates', data),
  deleteTemplate:   (id, cascade)  => api.delete(`/scheduling/templates/${id}`, { params: { cascade: cascade ? 1 : 0 } }),
  regenerateTemplate: (id, data)   => api.post(`/scheduling/templates/${id}/regenerate`, data || {}),
}

export const intakeApi = {
  listForms:        ()             => api.get('/intake/forms'),
  createForm:       (data)         => api.post('/intake/forms', data),
  updateForm:       (id, data)     => api.put(`/intake/forms/${id}`, data),
  deleteForm:       (id)           => api.delete(`/intake/forms/${id}`),
  listSubmissions:  ()             => api.get('/intake/submissions'),
  markReviewed:     (id)           => api.put(`/intake/submissions/${id}/review`),
}

// Lab orders (Phase 2 #12)
export const labOrdersApi = {
  list:        (params)            => api.get('/lab-orders', { params }),
  create:      (data)              => api.post('/lab-orders', data),
  update:      (id, data)          => api.put(`/lab-orders/${id}`, data),
  delete:      (id)                => api.delete(`/lab-orders/${id}`),
  addResult:   (id, data)          => api.post(`/lab-orders/${id}/results`, data),
}

// Prescriptions (Phase 2 #11) — internal documentation only
export const prescriptionsApi = {
  list:        (params)            => api.get('/prescriptions', { params }),
  create:      (data)              => api.post('/prescriptions', data),
  update:      (id, data)          => api.put(`/prescriptions/${id}`, data),
  delete:      (id)                => api.delete(`/prescriptions/${id}`),
}


// Templates
export const templatesApi = {
  browse:       (params)       => api.get('/templates', { params }),
  specialties:  ()             => api.get('/templates/specialties'),
  get:          (id)           => api.get(`/templates/${id}`),
  publish:      (data)         => api.post('/templates/publish', data),
  myTemplates:  ()             => api.get('/templates/my'),
  update:       (id, data)     => api.put(`/templates/${id}`, data),
  delete:       (id)           => api.delete(`/templates/${id}`),
  install:      (id)           => api.post(`/templates/${id}/install`),
  uninstall:    (id)           => api.delete(`/templates/${id}/install`),
  review:       (id, data)     => api.post(`/templates/${id}/review`, data),
  deleteReview: (id)           => api.delete(`/templates/${id}/review`),
}

// Exercise Prescriptions
export const exerciseApi = {
  get:      (meetingId)       => api.get(`/meetings/${meetingId}/exercises`),
  generate: (meetingId)       => api.post(`/meetings/${meetingId}/exercises/generate`, undefined, ai),
  update:   (meetingId, data) => api.put(`/meetings/${meetingId}/exercises`, data),
  delete:   (meetingId)       => api.delete(`/meetings/${meetingId}/exercises`),
}

// Session Highlights
export const highlightsApi = {
  list:     (meetingId)       => api.get(`/meetings/${meetingId}/highlights`),
  generate: (meetingId)       => api.post(`/meetings/${meetingId}/highlights/generate`, undefined, ai),
  add:      (meetingId, data) => api.post(`/meetings/${meetingId}/highlights`, data),
  delete:   (meetingId, id)   => api.delete(`/meetings/${meetingId}/highlights/${id}`),
}

// Pre-Auth Letters
export const preAuthApi = {
  list:     (meetingId)                 => api.get(`/meetings/${meetingId}/pre-auth-letters`),
  generate: (meetingId, data)           => api.post(`/meetings/${meetingId}/pre-auth-letters/generate`, data, ai),
  get:      (meetingId, letterId)       => api.get(`/meetings/${meetingId}/pre-auth-letters/${letterId}`),
  update:   (meetingId, letterId, data) => api.put(`/meetings/${meetingId}/pre-auth-letters/${letterId}`, data),
  delete:   (meetingId, letterId)       => api.delete(`/meetings/${meetingId}/pre-auth-letters/${letterId}`),
}

// Patient Portal
export const portalApi = {
  // Clinician endpoints
  getLink:        (patientId)       => api.get(`/patients/${patientId}/portal`),
  createLink:     (patientId, data) => api.post(`/patients/${patientId}/portal`, data),
  updateLink:     (patientId, data) => api.put(`/patients/${patientId}/portal`, data),
  deactivateLink: (patientId)       => api.delete(`/patients/${patientId}/portal`),
  getMessages:    (patientId)       => api.get(`/patients/${patientId}/portal/messages`),
  // Phase 2 #16 — supports an optional File attachment alongside the message.
  sendMessage:    (patientId, msg, file) => {
    const fd = new FormData()
    fd.append('message', msg)
    if (file) fd.append('file', file)
    return api.post(`/patients/${patientId}/portal/messages`, fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
  },
  // Attachments are auth- or PIN-gated routes on the API host, so a plain
  // <a href> can't open them — it landed on the web host's 404 page. Fetch
  // as a blob and hand the browser an object URL instead.
  downloadAttachment: (messageId)   => api.get(`/portal-messages/${messageId}/attachment`, { responseType: 'blob' }),
  // Public portal (no auth). Every protected portal endpoint re-verifies the
  // PIN, so every call has to carry it — without it the server answers
  // `requires_pin` with a 200 and a send silently goes nowhere.
  access:         (token, pin)      => api.get(`/portal/${token}`, { params: pin ? { pin } : {}, headers: portalPinHeader(pin) }),
  messages:       (token, pin)      => api.get(`/portal/${token}/messages`, { headers: portalPinHeader(pin) }),
  sendPatientMsg: (token, msg, file, pin) => {
    const fd = new FormData()
    fd.append('message', msg)
    if (file) fd.append('file', file)
    return api.post(`/portal/${token}/message`, fd, {
      headers: { 'Content-Type': 'multipart/form-data', ...portalPinHeader(pin) },
    })
  },
  markRead:       (token, pin)      => api.post(`/portal/${token}/messages/mark-read`, null, { headers: portalPinHeader(pin) }),
  downloadPatientAttachment: (token, messageId, pin) =>
    api.get(`/portal/${token}/messages/${messageId}/attachment`, { responseType: 'blob', headers: portalPinHeader(pin) }),
  // Wave 2 Session 2 — patient-portal self-booking. These hit
  // /portal/{token}/slots + /portal/{token}/book which already
  // exist on the backend (Phase 2 #13); this is just the wrapper.
  publicSlots:    (token)           => api.get(`/portal/${token}/slots`),
  publicBook:     (token, data)     => api.post(`/portal/${token}/book`, data),
  // Phase 2 #13 — patient-facing intake form fetch + submit. Backend
  // routes exist (`routes/api.php` lines 61-62 → SchedulingController::
  // publicIntakeForm / publicSubmitIntake) but no UI in the portal yet.
  // Wrappers live here so a future intake-form component can bind to a
  // stable API surface instead of hand-rolling axios each time.
  publicIntakeForm:   (token, formId)        => api.get(`/portal/${token}/intake/${formId}`),
  publicSubmitIntake: (token, formId, data)  => api.post(`/portal/${token}/intake/${formId}`, data),
}

// White-label
export const whiteLabelApi = {
  get:            ()     => api.get('/white-label'),
  save:           (data) => api.post('/white-label', data),
  uploadLogo:     (file) => { const f = new FormData(); f.append('logo', file); return api.post('/white-label/logo', f, { headers: { 'Content-Type': 'multipart/form-data' } }) },
  uploadFavicon:  (file) => { const f = new FormData(); f.append('favicon', file); return api.post('/white-label/favicon', f, { headers: { 'Content-Type': 'multipart/form-data' } }) },
  deleteLogo:     ()     => api.delete('/white-label/logo'),
  deleteFavicon:  ()     => api.delete('/white-label/favicon'),
  preview:        ()     => api.get('/white-label/preview'),
  deactivate:     ()     => api.delete('/white-label'),
  activate:       ()     => api.post('/white-label/activate'),
  resolve:        (host) => api.get('/white-label/resolve', { params: { host } }),
  checkSubdomain: (sub)  => api.get('/white-label/check-subdomain', { params: { subdomain: sub } }),
}

// Referral
export const referralApi = {
  dashboard:    ()     => api.get('/referral/dashboard'),
  validateCode: (code) => api.post('/referral/validate', { code }),
  applyCredits: (amt)  => api.post('/referral/apply-credits', { amount_cents: amt }),
  leaderboard:  ()     => api.get('/referral/leaderboard'),
}

// Clinic
export const clinicApi = {
  get:          ()             => api.get('/clinic'),
  create:       (data)         => api.post('/clinic', data),
  update:       (data)         => api.put('/clinic', data),
  addMember:    (data)         => api.post('/clinic/members', data),
  updateMember: (userId, data) => api.put(`/clinic/members/${userId}`, data),
  removeMember: (userId)       => api.delete(`/clinic/members/${userId}`),
  analytics:    (period)       => api.get('/clinic/analytics', { params: { period } }),
}

// Admin
export const adminApi = {
  dashboard:   ()             => api.get('/admin/dashboard'),
  revenue:     ()             => api.get('/admin/revenue'),
  aiUsage:     ()             => api.get('/admin/ai-usage'),
  productivity: ()            => api.get('/admin/productivity'),
  systemHealth: ()                  => api.get('/admin/system-health'),
  subscriptionLifecycle: ()         => api.get('/admin/subscriptions/lifecycle'),
  onboardingFunnel: ()              => api.get('/admin/onboarding-funnel'),
  broadcastSegments: ()             => api.get('/admin/broadcast/segments'),
  broadcastPreview: (segment)       => api.get('/admin/broadcast/preview', { params: { segment } }),
  broadcastSend: (data)             => api.post('/admin/broadcast', data),
  hipaa:           ()               => api.get('/admin/hipaa'),
  impersonate:    (userId)          => api.post(`/admin/impersonate/${userId}`),
  partnersList:   ()                => api.get('/admin/partners'),
  partnerShow:    (id)              => api.get(`/admin/partners/${id}`),
  partnerUpdate:  (id, data)        => api.put(`/admin/partners/${id}`, data),
  partnerRotateKey: (id)            => api.post(`/admin/partners/${id}/rotate-key`),
  usersBulk:      (data)            => api.post('/admin/users/bulk', data),

  listUsers:   (status)             => api.get(`/admin/users${status ? `?status=${status}` : ''}`),
  createUser:  (data)               => api.post('/admin/users', data),
  approveUser: (id)                 => api.put(`/admin/users/${id}/approve`),
  rejectUser:  (id)                 => api.delete(`/admin/users/${id}/reject`),
  updateRole:  (id, role)           => api.put(`/admin/users/${id}/role`, { role }),
  deleteUser:  (id)                 => api.delete(`/admin/users/${id}`),

  templatesIndex:    (params)       => api.get('/admin/templates', { params }),
  templateModerate:  (id, data)     => api.put(`/admin/templates/${id}`, data),
  templateDelete:    (id)           => api.delete(`/admin/templates/${id}`),

  plansList:         ()             => api.get('/admin/plans'),
  planUpdate:        (id, data)     => api.put(`/admin/plans/${id}`, data),

  bannersList:       ()             => api.get('/admin/banners'),
  bannerCreate:      (data)         => api.post('/admin/banners', data),
  bannerUpdate:      (id, data)     => api.put(`/admin/banners/${id}`, data),
  bannerDelete:      (id)           => api.delete(`/admin/banners/${id}`),

  flagsList:         ()             => api.get('/admin/feature-flags'),
  flagCreate:        (data)         => api.post('/admin/feature-flags', data),
  flagUpdate:        (id, data)     => api.put(`/admin/feature-flags/${id}`, data),
  flagDelete:        (id)           => api.delete(`/admin/feature-flags/${id}`),

  userExport:        (id)           => api.get(`/admin/users/${id}/export`, { responseType: 'blob' }),
}

// Public banner (no auth)
export const bannerApi = {
  active: () => api.get('/banner/active'),
}

// Audit Logs (HIPAA)
export const auditApi = {
  list:   (params) => api.get('/admin/audit-logs', { params }),
  stats:  (params) => api.get('/admin/audit-logs/stats', { params }),
  export: (params) => api.get('/admin/audit-logs/export', { params }),
}

// Public marketing form submissions (no auth, throttle:5/min on the server)
export const marketingApi = {
  contact:     (data) => api.post('/contact', data),
  demoRequest: (data) => api.post('/demo-request', data),
}

// Payment Configuration (Admin)
export const paymentConfigApi = {
  get:  ()              => api.get('/admin/payment-config'),
  save: (gateway, data) => api.post(`/admin/payment-config/${gateway}`, data),
  test: (gateway)       => api.post(`/admin/payment-config/${gateway}/test`),
}
